import data from "../../shared/sharedData";
export default {
  name: "Employer Point-of-Contact Information",
  symbol: "list",
  pageInfo: {
    text: `<b>Important note: </b> The information contained in this section is for an employee authorized to act on behalf of the employer in labor certification or labor condition application matters. The information in this section must be different from the attorney or agent information listed in Section D, except when an attorney listed in Section D is an employee of the employer.`,
    coloring: "grey",
  },
  sections: [
    {
      name: "Name & Title",
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Employer Point of Contact profile to populate this section (optional)",
          code: "B",
          prefill: {
            schema: "employerPocs",
            type: "employer-pocs",
            formName: "9141",
            formSection: "B",
          },
        },
        {
          code: "B.1",
          label: "Contact’s last (family) name",
          machine_name: "requestorPocLastName",
          widget: "text",
          tooltip:
            "Enter the last (family) name of the employer’s point of contact.",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Last Name must contain at least 2 characters.",
            },
            "noNAs",
          ],
          maxLength: 35,
        },
        {
          code: "B.2",
          label: "First (given) name",
          machine_name: "requestorPocFirstName",
          widget: "text",
          tooltip:
            "Enter the first (given) name of the employer’s point of contact.",
          validate: [
            "required",
            {
              minLength: 2,
              message: "First Name must contain at least 2 characters.",
            },
            "noNAs",
          ],
          maxLength: 35,
        },
        {
          code: "B.3",
          label: "Middle name(s) (if applicable) §",
          machine_name: "requestorPocMiddleName",
          widget: "text",
          tooltip:
            "Enter the middle name of the employer’s point of contact, if applicable.",
          maxLength: 35,
        },
        {
          code: "B.4",
          label: "Contact's job title",
          machine_name: "requestorPocJobTitle",
          widget: "text",
          tooltip: "Enter the job title of the employer’s point of contact.",
          validate: ["required", "noNAs"],
          maxLength: 60,
        },
      ],
    },
    {
      name: "Address",
      fields: [
        {
          code: "B.5",
          label: "Address 1",
          machine_name: "requestorPocAddr1",
          widget: "text",
          tooltip:
            "Enter the business street address for the employer’s point of contact.",
          validate: [
            "required",
            {
              minLength: 2,
              message: `Address 1 must contain at least 2 characters.`,
            },
            "noNAs",
          ],
          maxLength: 60,
        },
        {
          code: "B.6",
          label: "Address 2",
          machine_name: "requestorPocAddr2",
          widget: "text",
          tooltip:
            "If additional space is needed for the street address, use this line to complete the street address.",
          maxLength: 60,
        },
        {
          code: "B.10",
          label: "Country",
          machine_name: "requestorPocCountry",
          widget: "select",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["requestorPocState"],
          tooltip:
            "Enter the country of the employer’s point of contact. If the city and country are the same, the name must still be entered in both fields.",
        },
        {
          code: "B.7",
          label: "City",
          machine_name: "requestorPocCity",
          widget: "text",
          tooltip:
            "Enter the city of the employer’s point of contact. If the city and country are the same, the name must still be entered in both fields.",
          disabled: true,
          disableWhenField: "requestorPocCountry",
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
            "noNAs",
          ],
          maxLength: 35,
        },
        {
          code: "B.8",
          label: "State §",
          machine_name: "requestorPocState",
          widget: "select",
          selection: data.state,
          state: true,
          maxLength: 35,
          disabled: true,
          disableWhenField: "requestorPocCountry",
          isVisible: {
            when: "requestorPocCountry",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            {
              isRequired: "requestorPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          tooltip:
            "Enter the state of the employer’s point of contact. State is required if the country is United States.",
        },
        {
          code: "B.9",
          label: "Postal code",
          machine_name: "requestorPocPostalCode",
          widget: "text",
          disabled: true,
          disableWhenField: "requestorPocCountry",
          validate: [
            //"postal",
            {
              postalCountry: "requestorPocCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "requestorPocCountry",
              },
            },
            {
              isRequired: "requestorPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          //minLength: 5,
          maxLength: 10,
          tooltip:
            "Enter the postal (ZIP) code of the employer’s point of contact.",
        },
        {
          code: "B.11",
          machine_name: "requestorPocProvince",
          widget: "text",
          label: "Province (if applicable) §",
          disabled: true,
          disableWhenField: "requestorPocCountry",
          validate: [
            {
              isRequired: "requestorPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          maxLength: 25,
          tooltip:
            "Enter the province of the employer’s point of contact, if applicable.",
        },
      ],
    },
    {
      name: "Contact Information",
      fields: [
        {
          code: "B.12",
          label: "Telephone number",
          machine_name: "requestorPocPhone",
          widget: "phone",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and business telephone number of the employer’s point of contact. Include country code, if applicable.",
        },
        {
          code: "B.13",
          label: "Extension (if applicable) §",
          machine_name: "requestorPocPhoneExt",
          widget: "text",
          validate: ["number"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip:
            "Enter the extension of the telephone number of the employer’s point of contact, if applicable.",
        },
        {
          code: "B.14",
          label: "Business e-mail address",
          machine_name: "requestorPocEmail",
          widget: "text",
          validate: [
            "required",
            {
              email: {
                extraData: {
                  allowNAs: true,
                },
              },
            },
          ],
          tooltip:
            'Enter the business email address of the employer’s point of contact in the format name@emailaddress.top-level domain. The email entered in this field must be the same as the one regularly used by the employer’s point of contact for its business operations and capable of sending and receiving electronic communications from the Department with respect to the processing of this application. If the employer’s point of contact does not possess a business email address, please enter “N/A."',
          maxLength: 100,
        },
      ],
    },
  ],
};
